import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  LineHeightIcon,
  DownloadIcon,
  Pencil1Icon,
  PlusIcon,
  TrashIcon,
} from '@radix-ui/react-icons';
import { observer } from 'mobx-react-lite';

import { EyeClosedIcon, EyeOpenIcon } from '../../../components/icons';
import { DropdownMenu } from '../../../components/modals/dropdown-menu/DropdownMenu';
import { ScreenHeader } from '../../projects/components/screen-header';
import { AddBanner, bannerItems } from './add-banner';
import { RenumberShotsDialog } from '../../shots/components/renumber-shots.dialog';
import { RenameStepModal } from '../../process/components/rename-step.modal';
import { LimitReachedModal } from '../../../components/dialogs/limit-reached.modal';
import { useResponsive } from '../../../hooks/useResponsive';

import type { Project, Step } from '../../../app/entities';
import { useCurrentMember } from '../../../hooks/use-current-member';
import { SplitButton } from '../../../components/split-button/split-button';
import { openModal } from '../../../core/modal/open-modal';
import { DeleteDialog } from '../../../components/dialogs/DeleteDialog';

type ScheduleHeaderProps = {
  step: Step;
  project: Project;
};

export const ScheduleHeader: React.FC<ScheduleHeaderProps> = observer(({ step, project }) => {
  const { isDesktop } = useResponsive();
  const navigate = useNavigate();

  const view = step.resource;

  const [isExporting, setIsExporting] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isRenumberModalOpen, setIsRenumberModalOpen] = useState(false);
  const [showLimitReachedModal, setShowLimitReachedModal] = useState(false);

  const currentMember = useCurrentMember();
  const userStepPreferences = currentMember?.preferences.stepsUI[step._id];

  const stripboard = project.stripboard;

  const handleSelectBannerItem = ({ title, estimatedTime }: any) => {
    stripboard.addBannerStrip({
      position: 1,
      text: title,
      estimatedTime,
    });
  };

  const handleDownload = async (type: 'csv' | 'pdf') => {
    if (!project?.space?.canExport()) {
      return setShowLimitReachedModal(true);
    }

    if (isExporting) return;

    setIsExporting(true);

    await view.download(type);

    setIsExporting(false);
  };

  const shotsCount = useMemo(() => {
    if (!stripboard?.strips) {
      return 0;
    }

    return stripboard.strips.filter((strip) => strip.type === 'shot').length;
  }, [stripboard?.strips]);

  const items: React.ComponentProps<typeof DropdownMenu>['items'] = [
    {
      title:
        userStepPreferences?.showHiddenShots === false ? 'Show hidden shots' : 'Hide hidden shots',
      onSelect: () => currentMember?.toggleShowHiddenShots(step._id),
      icon: userStepPreferences?.showHiddenShots === false ? <EyeClosedIcon /> : <EyeOpenIcon />,
    },
    {
      title: 'Renumber shots',
      onSelect: () => setIsRenumberModalOpen(true),
      icon: <LineHeightIcon />,
    },
    {
      title: 'Add banner',
      icon: <PlusIcon />,
      disabled: !view?.options?.showBanner,
      subItems: bannerItems.map((item) => ({
        ...item,
        onSelect: () => handleSelectBannerItem(item),
      })),
    },
    ...(!isDesktop
      ? [
          {
            title: 'Generate PDF',
            icon: <DownloadIcon />,
            onSelect: () => stripboard.strips[0] && handleDownload('pdf'),
            disabled: isExporting || !shotsCount,
          },
        ]
      : []),
    {
      title: 'Rename step',
      icon: <Pencil1Icon />,
      onSelect: () => setIsRenameModalOpen(true),
    },
    {
      title: 'Delete step',
      icon: <TrashIcon />,
      type: 'danger' as const,
      onSelect: () => {
        openModal(DeleteDialog, {
          title: 'Delete step',
          text: `Are you sure you want to delete ${step.name}?`,
          onSubmit: () => {
            step.delete().then(() => navigate('../'));
          },
        });
      },
    },
  ];

  return (
    <>
      <ScreenHeader
        stepId={step._id}
        title={step.name}
        dueDate={step.dueDate}
        startDate={step.startDate}
        handleUpdateTimeFrame={(values) => step.update(values)}
        menu={<DropdownMenu items={items} size={'large'} />}
        actions={
          isDesktop && stripboard
            ? [
                !!stripboard && view?.options?.showBanner && (
                  <AddBanner key="add-banner" stripboard={stripboard} />
                ),
                !!stripboard && (
                  <SplitButton
                    text="Export"
                    icon={<DownloadIcon />}
                    items={[
                      {
                        title: 'Export as PDF',
                        onSelect: () => stripboard.strips[0] && handleDownload('pdf'),
                        disabled: isExporting || !shotsCount,
                      },
                      {
                        title: 'Export as CSV',
                        onSelect: () => stripboard.strips[0] && handleDownload('csv'),
                        disabled: isExporting || !shotsCount,
                      },
                    ]}
                  />
                ),
              ]
            : []
        }
      />

      {isRenumberModalOpen && (
        <RenumberShotsDialog
          stripboard={stripboard}
          onCancel={() => setIsRenumberModalOpen(false)}
        />
      )}

      {isRenameModalOpen && (
        <RenameStepModal step={step} onCancel={() => setIsRenameModalOpen(false)} />
      )}

      {showLimitReachedModal && (
        <LimitReachedModal
          title="Upgrade your space to export your schedule as a PDF"
          eventName="Export schedule attempt"
          onCancel={() => setShowLimitReachedModal(false)}
        />
      )}
    </>
  );
});
